/**
 * DexieTableAdapter — Dexie Table<T>를 IVaultTable<T>로 래핑
 *
 * Dexie가 이미 IVaultTable에 필요한 메서드를 모두 갖고 있으므로
 * 단순 위임(delegation)으로 구현.
 */

import type { Table, IndexableType } from "dexie";
import type { IVaultTable, IWhereClause, ICollection } from "../interfaces";

// ============================================================================
// DexieCollection — Dexie Collection/WhereClause 래핑
// ============================================================================

class DexieCollection<T> implements ICollection<T> {
  constructor(private _inner: any) {}

  filter(fn: (item: T) => boolean): ICollection<T> {
    return new DexieCollection(this._inner.filter(fn));
  }

  and(fn: (item: T) => boolean): ICollection<T> {
    return new DexieCollection(this._inner.and(fn));
  }

  toArray(): Promise<T[]> {
    return this._inner.toArray();
  }

  count(): Promise<number> {
    return this._inner.count();
  }

  first(): Promise<T | undefined> {
    return this._inner.first();
  }

  delete(): Promise<number> {
    return this._inner.delete();
  }
}

// ============================================================================
// DexieWhereClause
// ============================================================================

class DexieWhereClause<T> implements IWhereClause<T> {
  constructor(private _inner: any) {}

  equals(value: unknown): ICollection<T> {
    return new DexieCollection(this._inner.equals(value));
  }

  between(
    lower: unknown,
    upper: unknown,
    includeLower?: boolean,
    includeUpper?: boolean,
  ): ICollection<T> {
    return new DexieCollection(
      this._inner.between(lower, upper, includeLower, includeUpper),
    );
  }
}

// ============================================================================
// DexieTableAdapter
// ============================================================================

export class DexieTableAdapter<T, PK extends IndexableType = string> implements IVaultTable<T, PK> {
  constructor(private _table: Table<T, PK>) {}

  get(key: PK): Promise<T | undefined> {
    return this._table.get(key);
  }

  put(item: T): Promise<PK> {
    return this._table.put(item) as Promise<PK>;
  }

  add(item: T): Promise<PK> {
    return this._table.add(item) as Promise<PK>;
  }

  delete(key: PK): Promise<void> {
    return this._table.delete(key);
  }

  update(key: PK, changes: Partial<T>): Promise<number> {
    // Dexie 의 UpdateSpec<T> 는 Partial<T> 와 불변(invariant) 관계라 직접 대입이 안 된다 — 이 캐스팅만 불가피.
    return this._table.update(key, changes as Parameters<Table<T, PK>["update"]>[1]);
  }

  bulkPut(items: T[]): Promise<void> {
    return this._table.bulkPut(items).then(() => {});
  }

  bulkGet(keys: PK[]): Promise<(T | undefined)[]> {
    return this._table.bulkGet(keys);
  }

  bulkDelete(keys: PK[]): Promise<void> {
    return this._table.bulkDelete(keys);
  }

  toArray(): Promise<T[]> {
    return this._table.toArray();
  }

  clear(): Promise<void> {
    return this._table.clear();
  }

  count(): Promise<number> {
    return this._table.count();
  }

  where(indexName: string): IWhereClause<T> {
    return new DexieWhereClause(this._table.where(indexName));
  }

  orderBy(indexName: string): ICollection<T> {
    return new DexieCollection(this._table.orderBy(indexName));
  }
}
